/**
 * 어시스트 드로어 접기/펼치기 상태 — **뷰어 전용** 저장 키(D4).
 *
 * `useCoachCollapse` 를 공유하지 않는다: 같은 사람이 호스트와 참가자를
 * 오갈 때 두 화면의 취향이 독립이어야 한다는 저장소 규율 그대로다
 * (`useFeedOrder('viewer')`). 로직은 코치 훅과 같고 저장 키만 다르다.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

const STORAGE_KEY = 'omnitrans:assist-collapsed-viewer'

/** 이 폭 미만에서는 기본 접힘 — 자막이 본론이다. */
export const ASSIST_OVERLAY_MAX_WIDTH = 1024

function readStored(): boolean | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw === null ? null : raw === '1'
  } catch {
    return null
  }
}

function isNarrow(): boolean {
  if (typeof window === 'undefined') return false
  return window.innerWidth < ASSIST_OVERLAY_MAX_WIDTH
}

export function useAssistCollapse(): {
  collapsed: boolean
  narrow: boolean
  toggle: () => void
  setCollapsed: (collapsed: boolean) => void
} {
  const [narrow, setNarrow] = useState(isNarrow)
  const [collapsed, setCollapsedState] = useState<boolean>(() => {
    const stored = readStored()
    return stored ?? true
  })
  /** 사용자가 직접 정한 적이 있는지 — 창 크기 변화가 취향을 덮지 않게. */
  const explicitRef = useRef(readStored() !== null)

  useEffect(() => {
    const onResize = (): void => {
      setNarrow(isNarrow())
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const setCollapsed = useCallback((next: boolean) => {
    explicitRef.current = true
    setCollapsedState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next ? '1' : '0')
    } catch {
      // 저장 실패는 무시한다 — 이번 세션만 유지된다.
    }
  }, [])

  const toggle = useCallback(() => {
    setCollapsed(!collapsed)
  }, [collapsed, setCollapsed])

  return { collapsed, narrow, toggle, setCollapsed }
}
